import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "eventera.collectionsExpanded";

// Roots start collapsed, so what is worth remembering is what the user opened.
// Keyed by space: browsing someone else's collections must not overwrite the
// state for your own.
type Stored = Record<string, string[]>;

// Two caps, because nothing here is ever pruned against a live tree: a key for a
// collection since deleted or re-parented simply matches nothing. Both bounds
// are far above any real list.
const MAX_SPACES = 5;
const MAX_KEYS = 500;

function readStored(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

function readSpace(space: string): Set<string> {
  const keys = readStored()[space];
  return new Set(Array.isArray(keys) ? keys : []);
}

function writeSpace(space: string, keys: Set<string>) {
  try {
    const stored = readStored();
    delete stored[space];
    // Object key order is insertion order for string keys, so re-inserting the
    // active space last makes the oldest entry the one that falls off.
    const spaces = Object.keys(stored).slice(-(MAX_SPACES - 1));
    const next: Stored = {};
    for (const other of spaces) next[other] = stored[other];
    next[space] = [...keys].slice(-MAX_KEYS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A full or disabled storage costs the user persistence, not the view.
  }
}

/**
 * Which rows of the collections tree are open, persisted to localStorage.
 *
 * Deliberately not in Redux: nothing outside the sidebar reads it, and it has to
 * survive a reload, which Redux state here does not. Same reasoning as
 * {@link useRecordsViewOptions}.
 *
 * Members are row keys (root-to-node id paths), not collection ids — the same
 * collection under two parents opens and closes independently.
 */
export function useExpandedCollections(space: string) {
  const [state, setState] = useState(() => ({
    space,
    keys: readSpace(space),
  }));

  // Switching spaces swaps in that space's saved state, rather than carrying the
  // current one over and then saving it under the wrong key.
  if (state.space !== space) {
    setState({ space, keys: readSpace(space) });
  }

  useEffect(() => {
    writeSpace(state.space, state.keys);
  }, [state]);

  const update = useCallback(
    (next: (keys: Set<string>) => Set<string> | null) =>
      setState((previous) => {
        const keys = next(previous.keys);
        // Null means nothing changed; returning the same object keeps the write
        // effect and every downstream memo from running for no reason.
        return keys ? { ...previous, keys } : previous;
      }),
    [],
  );

  const toggle = useCallback(
    (key: string) =>
      update((keys) => {
        const next = new Set(keys);
        if (!next.delete(key)) next.add(key);
        return next;
      }),
    [update],
  );

  /** Additive: opens the way to a row without closing anything else. */
  const reveal = useCallback(
    (keys: string[]) =>
      update((current) => {
        if (keys.every((key) => current.has(key))) return null;
        const next = new Set(current);
        for (const key of keys) next.add(key);
        return next;
      }),
    [update],
  );

  const expandAll = useCallback(
    (keys: string[]) => update(() => new Set(keys)),
    [update],
  );

  const collapseAll = useCallback(
    () => update((keys) => (keys.size === 0 ? null : new Set<string>())),
    [update],
  );

  return { expanded: state.keys, toggle, reveal, expandAll, collapseAll };
}
